<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold mb-8">Search</h1>
    
    <!-- Search Section -->
    <div class="bg-white rounded-lg shadow-lg p-6 mb-8">
      <!-- Search Tabs -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in searchTabs"
            :key="tab.id"
            @click="switchTab(tab.id)"
            :class="[
              activeTab === tab.id
                ? 'border-primary-700 text-primary-800'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            ]"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Quick Search Content -->
      <div v-if="activeTab === 'quick'" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- TCR Search -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">TCR Sequence</label>
            <input
              v-model="tcrSequence"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
              placeholder="Enter CDR3α or CDR3β sequence..."
            />
          </div>

          <!-- Epitope Search -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Epitope Sequence</label>
            <input
              v-model="epitopeSequence"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
              placeholder="Enter epitope sequence..."
            />
          </div>

          <!-- Search Button -->
          <div class="flex items-end">
            <button
              @click="quickSearch"
              class="w-full bg-primary-700 text-white px-6 py-2 rounded-lg hover:bg-primary-800 transition-colors"
            >
              Quick Search
            </button>
          </div>
        </div>
      </div>

      <!-- Advanced Search Content -->
      <div v-else-if="activeTab === 'advanced'" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- TCR Panel -->
          <div class="bg-gray-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold mb-4 text-red-600">TCR</h3>
            <div class="space-y-4">
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Name</label>
                <select v-model="tcrName" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="name in tcrNames" :key="name" :value="name">{{ name }}</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">TRAV</label>
                <select v-model="trav" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="gene in travGenes" :key="gene" :value="gene">{{ gene }}</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">TRBV</label>
                <select v-model="trbv" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="gene in trbvGenes" :key="gene" :value="gene">{{ gene }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- MHC Panel -->
          <div class="bg-gray-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold mb-4 text-blue-600">MHC</h3>
            <div class="space-y-4">
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Class</label>
                <select v-model="mhcClass" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option value="I">I</option>
                  <option value="II">II</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">MHC A</label>
                <select v-model="mhcA" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="allele in mhcAs" :key="allele" :value="allele">{{ allele }}</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">MHC B</label>
                <select v-model="mhcB" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="allele in mhcBs" :key="allele" :value="allele">{{ allele }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Peptide Panel -->
          <div class="bg-gray-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold mb-4 text-green-700">Peptide</h3>
            <div class="space-y-4">
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Gene</label>
                <select v-model="epitopeGene" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="gene in epitopeGenes" :key="gene" :value="gene">{{ gene }}</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Mutation</label>
                <select v-model="epitopeMutation" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="mutation in epitopeMutations" :key="mutation" :value="mutation">{{ mutation }}</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Species</label>
                <select v-model="epitopeSpecies" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option v-for="species in epitopeSpeciesList" :key="species" :value="species">{{ species }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Function Panel -->
          <div class="bg-gray-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold mb-4 text-blue-600">Function</h3>
            <div class="space-y-4">
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Affinity</label>
                <select v-model="hasAffinity" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                  <option value="">Unknown</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Function</label>
                <select v-model="hasFunction" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                  <option value="">Unknown</option>
                </select>
              </div>
              <div class="flex items-center gap-4">
                <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">Method</label>
                <select v-model="evidenceMethod" class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
                  <option value="all">All</option>
                  <option value="cytokine release">Cytokine release</option>
                  <option value="activation marker">Activation marker</option>
                  <option value="cell killing">Cell killing</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <button
            @click="search"
            class="bg-primary-700 text-white px-6 py-2 rounded-lg hover:bg-primary-800 transition-colors"
          >
            Search
          </button>
        </div>
      </div>

      <!-- Structure Search Content -->
      <div v-else-if="activeTab === 'structure'" class="space-y-6">
        <!-- PDB Panel -->
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="flex items-center gap-4">
            <label class="text-sm font-medium text-gray-700 whitespace-nowrap w-12">PDB ID</label>
            <select v-model="pdbId" class="w-40 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent">
              <option value="all">All</option>
              <option v-for="id in pdbIds" :key="id" :value="id">{{ id }}</option>
            </select>
            <button
              @click="search"
              class="bg-primary-700 text-white px-6 py-2 rounded-lg hover:bg-primary-800 transition-colors whitespace-nowrap"
            >
              Search
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <i class="fas fa-exclamation-circle text-red-400 text-xl"></i>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error</h3>
          <div class="mt-2 text-sm text-red-700">
            {{ error }}
          </div>
          <div class="mt-4">
            <div class="-mx-2 -my-1.5 flex">
              <button
                type="button"
                @click="error = ''"
                class="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <div v-else-if="searchResults.length > 0" class="bg-white rounded-lg shadow-lg p-6">
      <div class="flex justify-between items-center mb-6">
        <div class="flex items-center space-x-4">
          <h2 class="text-xl font-semibold">Search Results</h2>
          <span class="text-sm text-gray-500">({{ searchResults.length }} results)</span>
        </div>
        <div class="flex space-x-2">
          <button
            @click="viewMode = 'card'"
            :class="[
              'px-4 py-2 rounded-lg transition-colors',
              viewMode === 'card' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            ]"
          >
            <i class="fas fa-th-large mr-2"></i>Card View
          </button>
          <button
            @click="viewMode = 'table'"
            :class="[
              'px-4 py-2 rounded-lg transition-colors',
              viewMode === 'table' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            ]"
          >
            <i class="fas fa-table mr-2"></i>Table View
          </button>
        </div>
      </div>

      <!-- Card View -->
      <div v-if="viewMode === 'card'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="tcr in paginatedResults" :key="tcr.complex_id" 
          class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
          <div class="p-6">
            <div class="flex justify-between items-start mb-4">
              <h3 class="text-xl font-bold text-gray-800">{{ tcr.tcr_name }}</h3>
              <span class="px-3 py-1 text-sm rounded-full" 
                :class="tcr.mhc_class === 'I' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'">
                {{ tcr.mhc_class }}
              </span>
            </div>
            
            <div class="space-y-4">
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <i class="fas fa-dna text-primary-600"></i>
                  <span class="text-sm font-medium text-gray-600">CDR3α:</span>
                  <span class="font-mono text-sm bg-gray-50 px-2 py-1 rounded">{{ tcr.cdr3a }}</span>
                </div>
                <div class="flex items-center space-x-2">
                  <i class="fas fa-dna text-primary-600"></i>
                  <span class="text-sm font-medium text-gray-600">CDR3β:</span>
                  <span class="font-mono text-sm bg-gray-50 px-2 py-1 rounded">{{ tcr.cdr3b }}</span>
                </div>
              </div>

              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <i class="fas fa-virus text-green-600"></i>
                  <span class="text-sm font-medium text-gray-600">Epitope Gene:</span>
                  <span class="text-sm">{{ tcr.epitope_gene }}</span>
                </div>
                <div class="flex items-center space-x-2">
                  <i class="fas fa-microscope text-blue-600"></i>
                  <span class="text-sm font-medium text-gray-600">Affinity:</span>
                  <span class="text-sm">{{ tcr.affinity_3d }} μM</span>
                </div>
              </div>

              <!-- <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">EC50_1:</span>
                <span class="text-sm">{{ tcr.ec50_1 ? parseFloat(tcr.ec50_1).toFixed(4) : 'N/A' }} μM</span>
              </div>
              <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">E_1:</span>
                <span class="text-sm">{{ tcr.e_1 ? parseFloat(tcr.e_1).toFixed(4) : 'N/A' }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">EC50_2:</span>
                <span class="text-sm">{{ tcr.ec50_2 ? parseFloat(tcr.ec50_2).toFixed(4) : 'N/A' }} μM</span>
              </div>
              <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">E_2:</span>
                <span class="text-sm">{{ tcr.e_2 ? parseFloat(tcr.e_2).toFixed(4) : 'N/A' }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">EC50_3:</span>
                <span class="text-sm">{{ tcr.ec50_3 ? parseFloat(tcr.ec50_3).toFixed(4) : 'N/A' }} μM</span>
              </div>
              <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">E_3:</span>
                <span class="text-sm">{{ tcr.e_3 ? parseFloat(tcr.e_3).toFixed(4) : 'N/A' }}</span>
              </div> -->

              <div class="flex items-center space-x-2">
                <i class="fas fa-microscope text-blue-600"></i>
                <span class="text-sm font-medium text-gray-600">PDB ID:</span>
                <a 
                  v-if="tcr.pdb_id"
                  :href="`/tcr/${tcr.complex_id}`"
                  class="text-primary-600 hover:text-primary-800 text-sm"
                >
                  {{ tcr.pdb_id }}
                </a>
                <span v-else class="text-gray-500 text-sm">N/A</span>
              </div>
            </div>

            <div class="mt-6 flex justify-end">
              <button
                @click="navigateToDetail(tcr.complex_id)"
                class="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
              >
                <i class="fas fa-info-circle mr-2"></i>
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Table View -->
      <div v-else class="bg-white rounded-xl shadow-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TCR Name</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CDR3α</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CDR3β</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Epitope Gene</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affinity (2D)</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affinity (3D)</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Function</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="tcr in paginatedResults" :key="tcr.complex_id" class="hover:bg-gray-50 transition-colors duration-150">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ tcr.tcr_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-mono bg-gray-50 px-2 py-1 rounded">{{ tcr.cdr3a }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-mono bg-gray-50 px-2 py-1 rounded">{{ tcr.cdr3b }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ tcr.epitope_gene }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ tcr.affinity_2d }} μM</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ tcr.affinity_3d }} μM</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ tcr.function || 'N/A' }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    @click="navigateToDetail(tcr.complex_id)"
                    class="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    <i class="fas fa-info-circle mr-1"></i>
                    Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 使用新的分页组件 -->
      <div class="mt-6">
        <Pagination
          v-model:currentPage="currentPage"
          :items-per-page="itemsPerPage"
          :total-items="searchResults.length"
        />
      </div>
    </div>

    <!-- No Results Message -->
    <div
      v-else-if="hasSearched"
      class="bg-white rounded-lg shadow-lg p-6 text-center text-gray-600"
    >
      No results found. Please try different search terms.
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { TCRComplex } from '@/api/tcr'
import { tcrApi } from '@/api/tcr'
import Pagination from '@/components/Pagination.vue'

const router = useRouter()
const activeTab = ref('quick')
const searchTabs = [
  { id: 'quick', name: 'Quick Search' },
  { id: 'advanced', name: 'Advanced Search' },
  { id: 'structure', name: 'Structure Search' }
]
const tcrName = ref('all')
const trav = ref('all')
const trbv = ref('all')
const mhcClass = ref('all')
const mhcA = ref('all')
const mhcB = ref('all')
const epitopeGene = ref('all')
const epitopeMutation = ref('all')
const epitopeSpecies = ref('all')
const hasAffinity = ref('all')
const hasFunction = ref('all')
const evidenceMethod = ref('')

const pdbId = ref('')
const searchResults = ref<TCRComplex[]>([])
const loading = ref(false)
const error = ref('')
const hasSearched = ref(false)
const viewMode = ref<'card' | 'table'>('card')
const searchTimeout = ref<number | null>(null)

// 分页相关状态
const currentPage = ref(1)
const itemsPerPage = ref(12) // 每页显示12条数据
const totalPages = ref(1)

// 计算当前页的数据
const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return searchResults.value.slice(start, end)
})

// 更新分页信息
const updatePagination = () => {
  totalPages.value = Math.ceil(searchResults.value.length / itemsPerPage.value)
  if (currentPage.value > totalPages.value) {
    currentPage.value = 1
  }
}

// 监听搜索结果变化，更新分页
watch(searchResults, () => {
  updatePagination()
})

// 切换页面
const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// 快速搜索变量
const tcrSequence = ref('')
const epitopeSequence = ref('')

// 搜索选项
const tcrNames = ref<string[]>([])
const travGenes = ref<string[]>([])
const trbvGenes = ref<string[]>([])
const mhcAs = ref<string[]>([])
const mhcBs = ref<string[]>([])
const pdbIds = ref<string[]>([])
const epitopeGenes = ref<string[]>([])
const epitopeMutations = ref<string[]>([])
const epitopeSpeciesList = ref<string[]>([])

// 添加请求超时处理
const fetchWithTimeout = async (url: string, options: RequestInit, timeout = 10000) => {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    })
    clearTimeout(id)
    return response
  } catch (err) {
    clearTimeout(id)
    throw err
  }
}

// 获取搜索选项
const fetchSearchOptions = async (retryCount = 0) => {
  try {
    loading.value = true
    error.value = '' // 清除之前的错误信息
    
    const data = await tcrApi.getSearchOptions()
    
    // 过滤掉空值和null值，并确保按升序排列
    tcrNames.value = data.tcr_names?.filter(Boolean) || []
    travGenes.value = data.trav_genes?.filter(Boolean) || []
    trbvGenes.value = data.trbv_genes?.filter(Boolean) || []
    mhcAs.value = data.mhc_a_alleles?.filter(Boolean) || []
    mhcBs.value = data.mhc_b_alleles?.filter(Boolean) || []
    pdbIds.value = data.pdb_ids?.filter(Boolean) || []
    epitopeGenes.value = data.epitope_genes?.filter(Boolean) || []
    epitopeMutations.value = data.epitope_mutations?.filter(Boolean) || []
    epitopeSpeciesList.value = data.epitope_species?.filter(Boolean) || []
    
    // 确保所有选项都按升序排列
    tcrNames.value.sort()
    travGenes.value.sort()
    trbvGenes.value.sort()
    mhcAs.value.sort()
    mhcBs.value.sort()
    pdbIds.value.sort()
    epitopeGenes.value.sort()
    epitopeMutations.value.sort()
    epitopeSpeciesList.value.sort()
  } catch (err) {
    console.error('Error fetching search options:', err)
    
    // 如果是网络错误且重试次数小于3次，则自动重试
    if (retryCount < 3 && err instanceof Error && 
        (err.message.includes('Unable to connect') || err.message.includes('timeout'))) {
      console.log(`Retrying... (${retryCount + 1}/3)`)
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))) // 递增延迟
      return fetchSearchOptions(retryCount + 1)
    }
    
    // 显示用户友好的错误信息
    if (err instanceof Error) {
      error.value = err.message
    } else {
      error.value = 'An unexpected error occurred while fetching search options.'
    }
  } finally {
    loading.value = false
  }
}

// 在组件挂载时获取搜索选项，并添加错误重试
onMounted(() => {
  fetchSearchOptions()
})

// 修改搜索函数
const search = async () => {
  if (!tcrName.value && !trav.value && !trbv.value && !mhcClass.value && !mhcA.value && !mhcB.value && !epitopeSequence.value && !epitopeGene.value && !pdbId.value && !epitopeMutation.value && !epitopeSpecies.value && !hasAffinity.value && !hasFunction.value && !evidenceMethod.value) {
    error.value = 'At least one search condition is required'
    return
  }

  loading.value = true
  error.value = ''
  hasSearched.value = true

  // 清除之前的超时
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }

  try {
    const params = new URLSearchParams()

    // TCR相关参数
    if (tcrName.value && tcrName.value !== 'all') {
      params.append('tcr_name', tcrName.value)
    }
    if (trav.value && trav.value !== 'all') {
      params.append('trav', trav.value)
    }
    if (trbv.value && trbv.value !== 'all') {
      params.append('trbv', trbv.value)
    }

    // MHC相关参数
    if (mhcClass.value && mhcClass.value !== 'all') {
      params.append('mhc_class', mhcClass.value)
    }
    if (mhcA.value && mhcA.value !== 'all') {
      params.append('mhc_a', mhcA.value)
    }
    if (mhcB.value && mhcB.value !== 'all') {
      params.append('mhc_b', mhcB.value)
    }

    // 表位相关参数
    if (epitopeSequence.value) {
      params.append('epitope_sequence', epitopeSequence.value)
    }
    if (epitopeGene.value && epitopeGene.value !== 'all') {
      params.append('epitope_gene', epitopeGene.value)
    }
    if (epitopeMutation.value && epitopeMutation.value !== 'all') {
      params.append('peptide_mutation', epitopeMutation.value)
    }
    if (epitopeSpecies.value && epitopeSpecies.value !== 'all') {
      params.append('epitope_species', epitopeSpecies.value)
    }

    // 功能相关参数
    if (hasAffinity.value) {
      params.append('has_affinity', hasAffinity.value)
    }
    if (hasFunction.value) {
      params.append('has_function', hasFunction.value)
    }
    if (evidenceMethod.value) {
      params.append('evidence_method', evidenceMethod.value)
    }

    // 结构相关参数
    if (pdbId.value && pdbId.value !== 'all') {
      params.append('pdb_id', pdbId.value)
    }

    // console.log('advanced search parameters:', params.toString())
    
    const response = await fetchWithTimeout(
      `/reactcr/api/tcr/search/?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      },
      15000 // 15秒超时
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    // console.log('advanced search results:', data)
    searchResults.value = data.results
    currentPage.value = 1 // 重置到第一页
  } catch (err) {
    console.error('Search error:', err)
    if (err instanceof Error) {
      if (err.name === 'AbortError') {
        error.value = 'Request timeout. Please try again.'
      } else if (err.message.includes('Network Error')) {
        error.value = 'Unable to connect to the server. Please check your network connection and try again.'
      } else {
        error.value = `Search failed: ${err.message}`
      }
    } else {
      error.value = 'An unexpected error occurred while searching.'
    }
  } finally {
    loading.value = false
  }
}

// 添加数据缓存
const searchCache = new Map<string, TCRComplex[]>()

// 修改快速搜索函数
const quickSearch = async () => {
  if (!tcrSequence.value && !epitopeSequence.value) {
    error.value = 'At least one search condition is required'
    return
  }

  const cacheKey = `quick_${tcrSequence.value}_${epitopeSequence.value}`
  if (searchCache.has(cacheKey)) {
    searchResults.value = searchCache.get(cacheKey)!
    return
  }

  loading.value = true
  error.value = ''
  hasSearched.value = true

  try {
    const params = new URLSearchParams()
    if (tcrSequence.value) {
      params.append('tcr_sequence', tcrSequence.value)
    }
    if (epitopeSequence.value) {
      params.append('epitope_sequence', epitopeSequence.value)
    }

    const response = await fetchWithTimeout(
      `/reactcr/api/tcr/search/?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      },
      15000
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    searchResults.value = data.results
    searchCache.set(cacheKey, data.results)
  } catch (err) {
    console.error('Quick search error:', err)
    if (err instanceof Error) {
      if (err.name === 'AbortError') {
        error.value = 'Request timeout. Please try again.'
      } else if (err.message.includes('Network Error')) {
        error.value = 'Unable to connect to the server. Please check your network connection and try again.'
      } else {
        error.value = `Search failed: ${err.message}`
      }
    } else {
      error.value = 'An unexpected error occurred while searching.'
    }
  } finally {
    loading.value = false
  }
}

const navigateToDetail = (id: string) => {
  // console.log('Navigating to TCR detail page, ID:', id)
  if (!id) {
    console.error('TCR ID is empty, cannot navigate')
    error.value = 'Cannot navigate to detail page: TCR ID is empty'
    return
  }
  try {
    router.push({
      name: 'TCRDetail',
      params: { id: id }
    })
  } catch (err) {
    console.error('Failed to navigate to TCR detail page:', err)
    error.value = 'Failed to navigate to detail page: Please try again later'
  }
}

// 添加切换标签的函数
const switchTab = (tabId: string) => {
  activeTab.value = tabId
  error.value = '' // 清除错误信息
  hasSearched.value = false // 重置搜索状态
  searchResults.value = [] // 清空搜索结果
}
</script> 